import React, { useState, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { SponsFIDashboard } from '../sponsfi/components/Dashboard/Dashboard.tsx';
import { SponsFINavigation } from '../sponsfi/components/Dashboard/Navigation.tsx';
import FSWorkQueueContainer from './FiscalSponsorDashboard/Queues/FSWorkQueueContainer';
import EventRequestQueue from './FiscalSponsorDashboard/Queues/EventRequestQueue';
import ExpenseRequestQueue from './FiscalSponsorDashboard/Queues/ExpenseRequestQueue';
import DocumentRequestQueue from './FiscalSponsorDashboard/Documents/DocumentRequestQueue';
import DocumentManagement from './FiscalSponsorDashboard/Documents/DocumentManagement';
import '../styles/dashboard.css';
import { trackEvent } from '../utils/analytics';
import CausesList from './Dashboard/CausesList/CausesList';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography,
  Paper,
  IconButton,
  Badge,
  Tooltip,
  styled
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Event as EventIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

// Import SponsFI feature components using relative paths
import { ChartOfAccounts } from '../sponsfi/components/Accounting/ChartOfAccounts.tsx';
import { JournalEntries } from '../sponsfi/components/Accounting/JournalEntries.tsx';
import { FundManagement } from '../sponsfi/components/Accounting/FundManagement.tsx';
import { Reports } from '../sponsfi/components/Reports/Reports.tsx';
import { Compliance } from '../sponsfi/components/Compliance/Compliance.tsx';
import { Grants } from '../sponsfi/components/Grants/Grants.tsx';
import { Settings } from '../sponsfi/components/Settings/Settings.tsx';
import UnifiedRequestForm from './FiscalSponsorDashboard/Expenses/UnifiedRequestForm';

// Styled components for the tabs
const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTab-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-selected': {
      color: '#ffffff',
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: '#00BCD4',
  }
}));

const StyledTab = styled(Tab)({
  color: '#ffffff !important',
  '&.Mui-selected': {
    color: '#ffffff !important',
  },
});

const Dashboard = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('main');
  const [operationsTab, setOperationsTab] = useState('queue');

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Using token:', token);
      
      const response = await fetch('/api/applications/pending-count', {
        headers: {
          'Authorization': `Bearer ${token || ''}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pending count');
      }

      const data = await response.json();
      console.log('Received count:', data.count);
      setPendingCount(data.count);
    } catch (error) {
      console.error('Error fetching pending count:', error);
      setError('Failed to fetch pending count');
    }
  };

  useEffect(() => {
    fetchPendingCount();
  }, []);

  const getCurrentFiscalSponsorId = () => {
    return "default-sponsor-id";
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-nav">
        <button 
          className={`nav-button ${activeSection === 'main' ? 'active' : ''}`}
          onClick={() => setActiveSection('main')}
        >
          Main Dashboard
        </button>
        <button 
          className={`nav-button ${activeSection === 'operations' ? 'active' : ''}`}
          onClick={() => setActiveSection('operations')}
        >
          Operations
        </button>
        <button 
          className={`nav-button ${activeSection === 'sponsfi' ? 'active' : ''}`}
          onClick={() => setActiveSection('sponsfi')}
        >
          SponsFI Platform
        </button>
      </div>

      {activeSection === 'main' ? (
        <>
          <div className="dashboard-stats">
            <div className="stat-card">
              <h3>Pending Applications</h3>
              {error ? (
                <p className="error-message">{error}</p>
              ) : (
                <>
                  <p className="stat-number">{pendingCount}</p>
                  <Link 
                    to="/portal/admin/review-applications"
                    className="review-link"
                  >
                    Review Applications
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="dashboard-causes">
            <h2>Active Causes</h2>
            <CausesList />
          </div>
        </>
      ) : activeSection === 'operations' ? (
        <div className="operations-container">
          <Paper 
            sx={{ 
              background: 'rgba(18, 18, 18, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              mb: 3
            }}
          >
            <StyledTabs 
              value={operationsTab}
              onChange={(e, newValue) => setOperationsTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <StyledTab 
                icon={<AssignmentIcon />} 
                label="Queue" 
                value="queue"
              />
              <StyledTab 
                icon={<EventIcon />} 
                label="Events" 
                value="events"
              />
              <StyledTab 
                icon={<AccountBalanceIcon />} 
                label="Expenses" 
                value="expenses"
              />
              <StyledTab 
                icon={<DescriptionIcon />} 
                label="Documents" 
                value="documents"
              />
            </StyledTabs>
          </Paper>

          {operationsTab === 'queue' && <FSWorkQueueContainer />}
          {operationsTab === 'events' && <EventRequestQueue />}
          {operationsTab === 'expenses' && (
            <Box sx={{ p: 2 }}>
              <UnifiedRequestForm />
            </Box>
          )}
          {operationsTab === 'documents' && (
            <Box sx={{ p: 2 }}>
              <DocumentRequestQueue />
              <DocumentManagement />
            </Box>
          )}
        </div>
      ) : (
        <div className="sponsfi-container">
          <SponsFINavigation />
          <div className="sponsfi-content">
            <Routes>
              <Route 
                path="/" 
                element={<SponsFIDashboard fiscalSponsorId={getCurrentFiscalSponsorId()} />} 
              />
              <Route path="/chart-of-accounts" element={<ChartOfAccounts />} />
              <Route path="/journal-entries" element={<JournalEntries />} />
              <Route path="/fund-management" element={<FundManagement />} />
              <Route path="/reports/*" element={<Reports />} />
              <Route path="/compliance/*" element={<Compliance />} />
              <Route path="/grants/*" element={<Grants />} />
              <Route path="/settings/*" element={<Settings />} />
            </Routes>
          </div>
        </div>
      )}
    </div>
  );
};

// Add these styles to your dashboard.css
const styles = `
.operations-container {
  background: rgba(18, 18, 18, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  margin: 20px;
  min-height: calc(100vh - 120px);
}

.nav-button {
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
  margin-right: 12px;
}

.nav-button:hover {
  background: rgba(0, 188, 212, 0.1);
  border-color: #00BCD4;
  color: white;
}

.nav-button.active {
  background: linear-gradient(45deg, #00BCD4, #4CAF50);
  border-color: transparent;
  color: white;
}

.dashboard-nav {
  display: flex;
  padding: 16px 24px;
  background: rgba(18, 18, 18, 0.8);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}
`;

export default Dashboard; 